import React, { useContext, useEffect } from 'react'
import ReactGA4 from 'react-ga4'
import TagManager from 'react-gtm-module'
import { AppContext } from 'contexts/AppProvider'
import { BrandTypeEnum } from 'brands'
import { HatoReactGA4GroupsEnum, HatoReactGA4GroupsEnumStr } from 'enums'

interface useHatoReactGA4Props {
  groups: HatoReactGA4GroupsEnumStr
}

const useHatoReactGA4: React.FC<useHatoReactGA4Props> = ({ groups }) => {
  const { brand } = useContext(AppContext)

  useEffect(() => {
    if (brand) {
      // Google Tag Manager
      const tagManagerArgs = {
        gtmId: process.env.REACT_APP_GOOGLE_ANALYTICS_HATOHUB_GTM,
      }
      TagManager.initialize(tagManagerArgs)

      // GA4
      let isMallTemplate: boolean = false

      switch (brand.type) {
        case BrandTypeEnum.THOUSAND_PRODUCTS:
        case BrandTypeEnum.MALL:
        case BrandTypeEnum.MALL_WITHOUT_GROCERY:
          isMallTemplate = true
          break
        default:
          isMallTemplate = false
      }

      // Consent setting have to set before initialize.
      ReactGA4.gtag('consent', 'default', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      })

      if (groups === HatoReactGA4GroupsEnum.hato_store) {
        const HatoGA4TrackingID = isMallTemplate
          ? process.env.REACT_APP_GOOGLE_ANALYTICS_HATOHUB_GA4_MALL
          : process.env.REACT_APP_GOOGLE_ANALYTICS_HATOHUB_GA4
        ReactGA4.initialize([
          {
            trackingId: HatoGA4TrackingID as string,
            gtagOptions: { groups: HatoReactGA4GroupsEnum.hato_store },
          },
          ...(brand.ga4?.hatoStoreKey
            ? [
                {
                  trackingId: brand.ga4.hatoStoreKey,
                  gtagOptions: { groups: HatoReactGA4GroupsEnum.hato_store },
                },
              ]
            : []),
        ])
      } else if (groups === HatoReactGA4GroupsEnum.hato_heart) {
        ReactGA4.initialize([
          {
            trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS_HATOHUB_GA4_HATO_HEART as string,
            gtagOptions: { groups: HatoReactGA4GroupsEnum.hato_heart },
          },
          ...(brand.ga4?.hatoHeartKey
            ? [
                {
                  trackingId: brand.ga4.hatoHeartKey,
                  gtagOptions: { groups: HatoReactGA4GroupsEnum.hato_heart },
                },
              ]
            : []),
        ])
      } else if (groups === HatoReactGA4GroupsEnum.dine_in) {
        ReactGA4.initialize([
          {
            trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS_HATOHUB_GA4_DINE_IN as string,
            gtagOptions: { groups: HatoReactGA4GroupsEnum.dine_in },
          },
          ...(brand.ga4?.dineInKey
            ? [
                {
                  trackingId: brand.ga4.dineInKey,
                  gtagOptions: { groups: HatoReactGA4GroupsEnum.dine_in },
                },
              ]
            : []),
          ...(brand.ga4?.brandDineInKey
            ? [
                {
                  trackingId: brand.ga4.brandDineInKey,
                  gtagOptions: { groups: HatoReactGA4GroupsEnum.dine_in },
                },
              ]
            : []),
        ])
      } else if (groups === HatoReactGA4GroupsEnum.crm) {
        if (brand.ga4?.crmKey) {
          ReactGA4.initialize([
            {
              trackingId: brand.ga4.crmKey,
              gtagOptions: { groups: HatoReactGA4GroupsEnum.crm },
            },
          ])
        }
      }
    }
  }, [brand, groups])

  return null
}

export default useHatoReactGA4
